import { DECK, cardPoints, winningPlay } from "./engine.js";
import { scopedStorageKey } from "./runtime-context.js";

const PRACTICE_KEY = scopedStorageKey("practice-history-v1");
const MAX_DEALS = 200;

function cardById(id) {
  return DECK.find((card) => card.id === id);
}

function ratio(count, total) {
  return total ? count / total : null;
}

export function enrichPlayDecision(decision) {
  if (decision?.type !== "play") return decision;
  const hand = decision.handBefore.map(cardById).filter(Boolean);
  const legal = decision.legalCards.map(cardById).filter(Boolean);
  const chosen = cardById(decision.chosenCard);
  const trick = decision.trickBefore.map((play) => ({ seat: play.seat, card: cardById(play.card) })).filter((play) => play.card);
  const leadSuit = trick[0]?.card.suit || null;
  const winner = trick.length ? winningPlay(trick, decision.trump) : null;
  const partnerWinningBefore = Boolean(winner && winner.seat % 2 === decision.seat % 2);
  const leadAce = leadSuit ? hand.find((card) => card.suit === leadSuit && card.rank === "A") : null;
  const aceDuckOpportunity = Boolean(leadAce && legal.some((card) => card.id === leadAce.id) && legal.length > 1);
  const ten = legal.find((card) => card.rank === "10" && card.suit !== decision.trump);
  const tenHoldOpportunity = Boolean(ten && legal.length > 1);
  const trumpHoldOpportunity = legal.some((card) => card.suit === decision.trump)
    && legal.some((card) => card.suit !== decision.trump);
  return {
    ...decision,
    features: {
      wasLead: trick.length === 0,
      ledTrump: trick.length === 0 && chosen?.suit === decision.trump,
      partnerWinningBefore,
      aceDuckOpportunity,
      aceDucked: aceDuckOpportunity && chosen?.id !== leadAce.id,
      tenHoldOpportunity,
      tenHeld: tenHoldOpportunity && chosen?.id !== ten.id,
      trumpHoldOpportunity,
      trumpHeld: trumpHoldOpportunity && chosen?.suit !== decision.trump,
      loadedPartner: partnerWinningBefore && trick.length === 3 && cardPoints(chosen, decision.trump) >= 10,
    },
  };
}

export function loadPracticeHistory() {
  try {
    const value = JSON.parse(localStorage.getItem(PRACTICE_KEY));
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

export function savePracticeDeal(deal) {
  const history = [deal, ...loadPracticeHistory().filter((item) => item.id !== deal.id)].slice(0, MAX_DEALS);
  try {
    localStorage.setItem(PRACTICE_KEY, JSON.stringify(history));
  } catch {
    // Le journal principal reste la seconde copie structurée.
  }
  return history;
}

export function summarizePracticeStyle(history = loadPracticeHistory()) {
  const decisions = history.flatMap((deal) => deal.decisions || []);
  const plays = decisions.filter((decision) => decision.type === "play").map(enrichPlayDecision);
  const bids = decisions.filter((decision) => decision.type === "bid");
  const leads = plays.filter((decision) => decision.features.wasLead);
  const aceDucks = plays.filter((decision) => decision.features.aceDuckOpportunity);
  const tenHolds = plays.filter((decision) => decision.features.tenHoldOpportunity);
  const trumpHolds = plays.filter((decision) => decision.features.trumpHoldOpportunity);
  const partnerLoads = plays.filter((decision) => decision.features.partnerWinningBefore && decision.trickBefore.length === 3);
  const firstRoundBids = bids.filter((decision) => decision.round === 1);
  return {
    deals: history.length,
    decisions: decisions.length,
    plays: plays.length,
    bids: bids.length,
    directTakeRate: ratio(firstRoundBids.filter((decision) => decision.action === "take").length, firstRoundBids.length),
    trumpLeadRate: ratio(leads.filter((decision) => decision.features.ledTrump).length, leads.length),
    aceDuckRate: ratio(aceDucks.filter((decision) => decision.features.aceDucked).length, aceDucks.length),
    tenHoldRate: ratio(tenHolds.filter((decision) => decision.features.tenHeld).length, tenHolds.length),
    trumpHoldRate: ratio(trumpHolds.filter((decision) => decision.features.trumpHeld).length, trumpHolds.length),
    partnerLoadRate: ratio(partnerLoads.filter((decision) => decision.features.loadedPartner).length, partnerLoads.length),
  };
}
